"use client";

import { ArrowLeft, Heart, MessageSquare, Users, TrendingUp, Star, Plus } from 'lucide-react';
import Link from 'next/link';
import { ImageWithFallback } from '@/figma/components/figma/ImageWithFallback';
import type { CommunityIcon, CommunityPageContent } from '@/lib/page-content-types';

const features = [
  {
    icon: 'message' as CommunityIcon,
    title: 'Forum & Discussions',
    description: 'Join conversations about Israeli wine. Share tasting notes, ask questions, and learn from fellow enthusiasts.',
    image: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800'
  },
  {
    icon: 'heart' as CommunityIcon,
    title: 'Personal Cellar',
    description: 'Track your wine collection with detailed tasting notes, purchase history, and optimal drinking windows.',
    image: 'https://images.unsplash.com/photo-1765850258622-9d9afa7cf4e2?w=800'
  },
  {
    icon: 'star' as CommunityIcon,
    title: 'Rate & Review',
    description: 'Share your wine experiences and contribute to our community-driven rating system alongside expert scores.',
    image: 'https://images.unsplash.com/photo-1641453288558-6dce96ee996c?w=800'
  },
  {
    icon: 'users' as CommunityIcon,
    title: 'Connect',
    description: 'Follow fellow enthusiasts, share recommendations, and discover wines through trusted community members.',
    image: 'https://images.unsplash.com/photo-1543418219-44e30b057fea?w=800'
  },
  {
    icon: 'trend' as CommunityIcon,
    title: 'Wishlists & Alerts',
    description: 'Save wines you want to try and get notified about new vintages, availability, and special releases.',
    image: 'https://images.unsplash.com/photo-1516594915697-87eb3b1c14ea?w=800'
  }
];

const communityIconMap = {
  message: MessageSquare,
  heart: Heart,
  star: Star,
  users: Users,
  trend: TrendingUp,
} as const;

const recentActivity = [
  {
    user: 'David L.',
    action: 'rated',
    wine: 'Yatir Forest 2018',
    rating: 94,
    note: 'Stunning complexity. Dark fruit, tobacco, and Mediterranean herbs. Drinking beautifully now.',
    time: '2 hours ago'
  },
  {
    user: 'Sarah M.',
    action: 'added to cellar',
    wine: 'Castel Grand Vin 2017',
    bottles: 6,
    note: 'Laying these down for 5-10 years. Perfect vintage.',
    time: '5 hours ago'
  },
  {
    user: 'Michael R.',
    action: 'reviewed',
    wine: 'Tzora Shoresh 2019',
    rating: 92,
    note: 'Beautiful mineral character. Limestone terroir really shines through.',
    time: '1 day ago'
  },
  {
    user: 'Rachel K.',
    action: 'created wishlist',
    wines: ['Clos de Gat Syrah 2020', 'Recanati Reserve Cabernet'],
    note: 'Birthday wishlist - hunting for these!',
    time: '2 days ago'
  }
];

const topContributors = [
  { name: 'Jonathan S.', reviews: 247, followers: 1832, badge: 'Expert Taster' },
  { name: 'Emma D.', reviews: 189, followers: 1456, badge: 'Certified Sommelier' },
  { name: 'David L.', reviews: 156, followers: 892, badge: 'Wine Educator' },
  { name: 'Sarah M.', reviews: 134, followers: 743, badge: 'Collector' }
];

const trendingWines = [
  {
    name: 'Yatir Forest 2018',
    winery: 'Yatir Winery',
    reviews: 156,
    avgRating: 94.2,
    trend: '+12'
  },
  {
    name: 'Castel Grand Vin 2017',
    winery: 'Domaine du Castel',
    reviews: 203,
    avgRating: 95.8,
    trend: '+8'
  },
  {
    name: 'Clos de Gat Syrah 2020',
    winery: 'Clos de Gat',
    reviews: 87,
    avgRating: 93.5,
    trend: '+15'
  }
];

const benefits = [
  {
    title: 'Build Your Digital Cellar',
    points: [
      'Track bottles with photos and tasting notes',
      'Monitor optimal drinking windows',
      'Export your collection data',
      'Share selected wines with friends'
    ]
  },
  {
    title: 'Contribute Your Expertise',
    points: [
      'Rate wines on a 100-point scale',
      'Write detailed tasting notes',
      'Upload food pairing suggestions',
      'See how your ratings compare to critics'
    ]
  },
  {
    title: 'Discover & Connect',
    points: [
      'Follow users with similar taste profiles',
      'Get personalized wine recommendations',
      'Join regional tasting groups',
      'Participate in community events'
    ]
  },
  {
    title: 'Stay Informed',
    points: [
      'New vintage release notifications',
      'Price alerts for wishlist wines',
      'Winery event announcements',
      'Monthly community newsletter'
    ]
  }
];

export function CommunityPage({ content }: { content?: CommunityPageContent | null }) {
  const featuresData = content?.features?.length ? content.features : features;
  const recentActivityData = content?.recentActivity?.length ? content.recentActivity : recentActivity;
  const topContributorsData = content?.topContributors?.length ? content.topContributors : topContributors;
  const trendingWinesData = content?.trendingWines?.length ? content.trendingWines : trendingWines;
  const benefitsData = content?.benefits?.length ? content.benefits : benefits;

  return (
    <div className="min-h-screen" style={{ fontFamily: 'var(--font-body)' }}>
      {/* Back navigation */}
      <div style={{ backgroundColor: 'var(--linen)', borderBottom: '1px solid var(--stone)' }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16 py-8">
          <Link 
            href="/"
            className="inline-flex items-center gap-2 transition-opacity duration-200 hover:opacity-50"
            style={{ 
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-sm)',
              color: 'var(--charcoal)',
              fontWeight: 400
            }}
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section style={{ backgroundColor: 'var(--paper)' }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16">
          <div className="py-24 lg:py-32">
            <div className="max-w-4xl">
              <div className="mb-8">
                <span 
                  style={{ 
                    fontFamily: 'var(--font-display)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 300,
                    color: 'var(--slate)',
                    letterSpacing: '0.05em'
                  }}
                >
                  04
                </span>
              </div>
              
              <h1 
                className="mb-8"
                style={{ 
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--text-5xl)',
                  fontWeight: 300,
                  lineHeight: 'var(--line-height-tight)',
                  color: 'var(--charcoal)',
                  letterSpacing: '-0.02em'
                }}
              >
                Community
              </h1>
              
              <p 
                style={{ 
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-xl)',
                  lineHeight: 'var(--line-height-body)',
                  color: 'var(--slate)',
                  fontWeight: 300,
                  maxWidth: '720px'
                }}
              >
                Connect with fellow wine enthusiasts, track your personal cellar, share tasting notes, and discover new wines through trusted community recommendations.
              </p>

              <button 
                className="mt-12 px-8 py-4 transition-opacity duration-200 hover:opacity-80"
                style={{ 
                  backgroundColor: 'var(--charcoal)',
                  color: 'var(--paper)',
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-base)',
                  fontWeight: 400
                }}
              >
                Join the Community
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section style={{ backgroundColor: 'var(--linen)' }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16">
          <div className="py-24 lg:py-32">
            <div className="grid gap-8 md:grid-cols-2">
              {featuresData.map((feature) => {
                const FeatureIcon = communityIconMap[feature.icon];
                return (
                <Link
                  key={feature.title}
                  href={feature.title === "Forum & Discussions" ? "/community/forum" : "/community"}
                  style={{ backgroundColor: 'var(--paper)' }}
                  className="overflow-hidden group cursor-pointer transition-all duration-300 hover:shadow-lg"
                >
                  <div className="aspect-[16/9] overflow-hidden" style={{ backgroundColor: 'var(--stone)' }}>
                    <ImageWithFallback
                      src={feature.image}
                      alt={feature.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-10">
                    <div 
                      className="w-12 h-12 flex items-center justify-center mb-6"
                      style={{ backgroundColor: 'var(--linen)' }}
                    >
                      <FeatureIcon size={24} style={{ color: 'var(--oxblood)' }} />
                    </div>
                    <h3 
                      className="mb-4"
                      style={{ 
                        fontFamily: 'var(--font-display)',
                        fontSize: 'var(--text-2xl)',
                        fontWeight: 300,
                        color: 'var(--charcoal)'
                      }}
                    >
                      {feature.title}
                    </h3>
                    <p 
                      style={{ 
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-base)',
                        lineHeight: 'var(--line-height-body)',
                        color: 'var(--slate)',
                        fontWeight: 300
                      }}
                    >
                      {feature.description}
                    </p>
                  </div>
                </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Recent Activity */}
      <section style={{ backgroundColor: 'var(--paper)' }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16">
          <div className="py-24 lg:py-32">
            <div className="grid gap-16 lg:grid-cols-3">
              {/* Activity Feed */}
              <div className="lg:col-span-2">
                <div className="mb-12">
                  <h2 
                    className="mb-4"
                    style={{ 
                      fontFamily: 'var(--font-display)',
                      fontSize: 'var(--text-3xl)',
                      fontWeight: 300,
                      lineHeight: 'var(--line-height-tight)',
                      color: 'var(--charcoal)',
                      letterSpacing: '-0.02em'
                    }}
                  >
                    Recent Activity
                  </h2>
                  <p 
                    style={{ 
                      fontFamily: 'var(--font-body)',
                      fontSize: 'var(--text-base)',
                      lineHeight: 'var(--line-height-body)',
                      color: 'var(--slate)',
                      fontWeight: 300
                    }}
                  >
                    See what the community is tasting and discussing
                  </p>
                </div>

                <div className="space-y-6">
                  {recentActivityData.map((activity, index) => (
                    <div 
                      key={index}
                      className="p-8 border transition-colors duration-200 hover:bg-[var(--linen)]"
                      style={{ borderColor: 'var(--stone)' }}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <span 
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-base)',
                              color: 'var(--charcoal)',
                              fontWeight: 500
                            }}
                          >
                            {activity.user}
                          </span>
                          <span 
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-base)',
                              color: 'var(--slate)',
                              fontWeight: 300,
                              marginLeft: '0.5rem'
                            }}
                          >
                            {activity.action}
                          </span>
                        </div>
                        <span 
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-sm)',
                            color: 'var(--slate)',
                            fontWeight: 400
                          }}
                        >
                          {activity.time}
                        </span>
                      </div>

                      <h4 
                        className="mb-3"
                        style={{ 
                          fontFamily: 'var(--font-display)',
                          fontSize: 'var(--text-lg)',
                          fontWeight: 300,
                          color: 'var(--charcoal)'
                        }}
                      >
                        {activity.wine || activity.wines?.join(', ')}
                      </h4>

                      {activity.rating && (
                        <div className="flex items-center gap-2 mb-3">
                          <Star size={16} style={{ color: 'var(--oxblood)', fill: 'var(--oxblood)' }} />
                          <span 
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-base)',
                              color: 'var(--charcoal)',
                              fontWeight: 500
                            }}
                          >
                            {activity.rating}
                          </span>
                        </div>
                      )}

                      {activity.bottles && (
                        <p 
                          className="mb-3"
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-sm)',
                            color: 'var(--slate)',
                            fontWeight: 400
                          }}
                        >
                          {activity.bottles} bottles
                        </p>
                      )}

                      <p 
                        style={{ 
                          fontFamily: 'var(--font-body)',
                          fontSize: 'var(--text-sm)',
                          lineHeight: 'var(--line-height-body)',
                          color: 'var(--slate)',
                          fontWeight: 300,
                          fontStyle: 'italic'
                        }}
                      >
                        &ldquo;{activity.note}&rdquo;
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1">
                {/* Top Contributors */}
                <div className="mb-12">
                  <h3 
                    className="mb-6"
                    style={{ 
                      fontFamily: 'var(--font-display)',
                      fontSize: 'var(--text-xl)',
                      fontWeight: 300,
                      color: 'var(--charcoal)'
                    }}
                  >
                    Top Contributors
                  </h3>
                  <div className="space-y-4">
                    {topContributorsData.map((contributor) => (
                      <div 
                        key={contributor.name}
                        className="p-4 border cursor-pointer transition-colors duration-200 hover:bg-[var(--linen)]"
                        style={{ borderColor: 'var(--stone)' }}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span 
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-base)',
                              color: 'var(--charcoal)',
                              fontWeight: 500
                            }}
                          >
                            {contributor.name}
                          </span>
                        </div>
                        <span 
                          className="inline-block px-2 py-1 mb-2"
                          style={{ 
                            backgroundColor: 'var(--oxblood)',
                            color: 'var(--paper)',
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-xs)',
                            fontWeight: 400,
                            letterSpacing: '0.05em'
                          }}
                        >
                          {contributor.badge.toUpperCase()}
                        </span>
                        <div className="flex items-center gap-4">
                          <span 
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-xs)',
                              color: 'var(--slate)',
                              fontWeight: 400
                            }}
                          >
                            {contributor.reviews} reviews
                          </span>
                          <span style={{ color: 'var(--stone)' }}>•</span>
                          <span 
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-xs)',
                              color: 'var(--slate)',
                              fontWeight: 400
                            }}
                          >
                            {contributor.followers.toLocaleString()} followers
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Trending Wines */}
                <div>
                  <h3 
                    className="mb-6"
                    style={{ 
                      fontFamily: 'var(--font-display)',
                      fontSize: 'var(--text-xl)',
                      fontWeight: 300,
                      color: 'var(--charcoal)'
                    }}
                  >
                    Trending This Week
                  </h3>
                  <div className="space-y-4">
                    {trendingWinesData.map((wine) => (
                      <div 
                        key={wine.name}
                        className="p-4 cursor-pointer transition-all duration-300 hover:shadow-md"
                        style={{ backgroundColor: 'var(--linen)' }}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h4 
                            style={{ 
                              fontFamily: 'var(--font-display)',
                              fontSize: 'var(--text-base)',
                              fontWeight: 300,
                              color: 'var(--charcoal)'
                            }}
                          >
                            {wine.name}
                          </h4>
                          <span 
                            className="flex items-center gap-1 ml-2"
                            style={{ 
                              color: 'var(--oxblood)',
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-xs)',
                              fontWeight: 500,
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <TrendingUp size={12} />
                            {wine.trend}
                          </span>
                        </div>
                        <p 
                          className="mb-3"
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-xs)',
                            color: 'var(--slate)',
                            fontWeight: 400
                          }}
                        >
                          {wine.winery}
                        </p>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1">
                            <Star size={12} style={{ color: 'var(--oxblood)', fill: 'var(--oxblood)' }} />
                            <span 
                              style={{ 
                                fontFamily: 'var(--font-body)',
                                fontSize: 'var(--text-sm)',
                                color: 'var(--charcoal)',
                                fontWeight: 500
                              }}
                            >
                              {wine.avgRating}
                            </span>
                          </div>
                          <span 
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-xs)',
                              color: 'var(--slate)',
                              fontWeight: 400
                            }}
                          >
                            {wine.reviews} reviews
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section style={{ backgroundColor: 'var(--linen)' }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16">
          <div className="py-24 lg:py-32">
            <div className="mb-16 text-center max-w-3xl mx-auto">
              <h2 
                className="mb-4"
                style={{ 
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--text-3xl)',
                  fontWeight: 300,
                  lineHeight: 'var(--line-height-tight)',
                  color: 'var(--charcoal)',
                  letterSpacing: '-0.02em'
                }}
              >
                Why Join Our Community?
              </h2>
              <p 
                style={{ 
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-base)',
                  lineHeight: 'var(--line-height-body)',
                  color: 'var(--slate)',
                  fontWeight: 300
                }}
              >
                Free membership includes powerful tools for collectors and enthusiasts
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2">
              {benefitsData.map((benefit) => (
                <div 
                  key={benefit.title}
                  className="p-10"
                  style={{ backgroundColor: 'var(--paper)' }}
                >
                  <h3 
                    className="mb-6"
                    style={{ 
                      fontFamily: 'var(--font-display)',
                      fontSize: 'var(--text-xl)',
                      fontWeight: 300,
                      color: 'var(--charcoal)'
                    }}
                  >
                    {benefit.title}
                  </h3>
                  <ul className="space-y-3">
                    {benefit.points.map((point) => (
                      <li 
                        key={point}
                        className="flex items-start gap-3"
                      >
                        <Plus 
                          size={16} 
                          className="flex-shrink-0 mt-1"
                          style={{ color: 'var(--oxblood)' }} 
                        />
                        <span 
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-base)',
                            lineHeight: 'var(--line-height-body)',
                            color: 'var(--slate)',
                            fontWeight: 300
                          }}
                        >
                          {point}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="mt-16 text-center">
              <button 
                className="px-12 py-5 transition-opacity duration-200 hover:opacity-80"
                style={{ 
                  backgroundColor: 'var(--charcoal)',
                  color: 'var(--paper)',
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 400
                }}
              >
                Create Free Account
              </button>
              <p 
                className="mt-4"
                style={{ 
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--slate)',
                  fontWeight: 300
                }}
              >
                Join 12,000+ wine enthusiasts exploring Israeli wine
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
